import { SubscriptionCategory } from '../../../common/enums';
import { isExternalSubscription } from '../../../common/utils/subscriptionUtils';
import { ChangeOwnerError } from '../../../generated/api/changeOwnerError';
import { CompanyInfoResponse } from '../../../generated/api/companyInfoResponse';
import { SubscriptionPbxDetails } from '../../../generated/api/subscriptionPbxDetails';
import { SubscriptionType } from '../../../generated/api/subscriptionType';
import { getSubscriptionTypes } from '../../../public/common/util/category';
import { isOneTimeFeeDevice } from '../subscriptionDetailsUtils';
import type { Subscription } from '../../../generated/api/subscription';

const CHANGE_OWNER_BLOCKED: ChangeOwnerError = {
  reason: ChangeOwnerError.ReasonEnum.VOICE_VAKIO_OR_RING,
};

const BLOCKED_SUBSCRIPTION_TYPES = [
  SubscriptionType.SPECIAL_NUMBER,
  SubscriptionType.MOBILE_PBX_LITE,
  SubscriptionType.MOBILE_PBX,
];

export const validateSubscriptionForChangeOwner = (subscription: Subscription): ChangeOwnerError | undefined => {
  if (BLOCKED_SUBSCRIPTION_TYPES.includes(subscription.subscriptionType)) {
    return CHANGE_OWNER_BLOCKED;
  }

  if (subscription.subscriptionType === SubscriptionType.MOBILE) {
    const pbxConfig = subscription.details?.mobile?.pbxConfiguration;
    const pbxType = pbxConfig?.pbxConfigurationDetails?.pbxType;

    const isRing = pbxType === SubscriptionPbxDetails.PbxTypeEnum.RING;
    const isVakioWithCorporateAndExtension =
      pbxType === SubscriptionPbxDetails.PbxTypeEnum.VAKIO &&
      !!pbxConfig?.corporateNumber &&
      !!pbxConfig?.extensionNumber;

    if (isRing || isVakioWithCorporateAndExtension) {
      return CHANGE_OWNER_BLOCKED;
    }
  }

  return undefined;
};

export const getMandatoryFieldRules = (
  subscription: Subscription,
  companyInfo?: CompanyInfoResponse
): {
  isCostCenterMandatory: boolean;
  isEmailMandatory: boolean;
  isPhoneNumberMandatory: boolean;
} => {
  const isDeviceSubscription = subscription.subscriptionType === SubscriptionType.DEVICE;
  const isInternalCustomer = companyInfo?.customerType === CompanyInfoResponse.CustomerTypeEnum.INTERNAL_CUSTOMERS;
  const isEppSubscription = !!subscription.details?.device?.eppSubscription;

  return {
    isCostCenterMandatory: isDeviceSubscription && isInternalCustomer,
    isEmailMandatory: isDeviceSubscription && (isEppSubscription || isInternalCustomer),
    isPhoneNumberMandatory: isDeviceSubscription && (isEppSubscription || isInternalCustomer),
  };
};

export const isSubscriptionEditable = (subscription: Subscription): boolean => {
  return (
    !getSubscriptionTypes(SubscriptionCategory.SERVICE).includes(subscription.subscriptionType) &&
    !isExternalSubscription(subscription) &&
    !isOneTimeFeeDevice(subscription) &&
    subscription.subscriptionType !== SubscriptionType.SPECIAL_NUMBER
  );
};

export const shouldDisableField = (error?: ChangeOwnerError): boolean => {
  return ChangeOwnerError.ReasonEnum.VOICE_VAKIO_OR_RING === error?.reason;
};
